import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import ClientBase from "@citrine/client";
import { UnauthorizedError } from "@citrine/client/utils";

import type { IProject } from "./project";

interface SearchValue {
  value: string;
  search_method: "EXACT" | "SUBSTRING";
}

export interface ProjectQuery {
  archived?: boolean;
  page?: number;
  per_page?: number;
  sort_by?: {
    field: "name" | "created_at";
    order: "asc" | "desc";
  };
  search_params?: {
    name?: SearchValue;
    description?: SearchValue;
  };
}

export class ProjectClient extends ClientBase<IProject, string, ProjectQuery> {
  protected constructor(public readonly teamId: string | undefined) {
    super({
      endpoint: teamId
        ? `/api/v3/teams/${teamId}/projects`
        : "/api/v3/projects",
      getField: "project",
      findField: "projects",
      defaultQuery: {
        archived: undefined,
        page: 1,
        // these objects are lightweight, so we can request more than 20 at a time
        per_page: 100,
        sort_by: undefined,
        search_params: undefined,
      },
    });
  }

  static getInstance(teamId?: string) {
    return new this(teamId);
  }

  getUri(id: string) {
    return `/api/v3/projects/${id}`;
  }

  async archive(model: IProject): Promise<IProject> {
    const result = { ...model, archived: true };
    this.addModelToCache(result);
    await this.request(this.archiveConfig(model));
    this.invalidateAfterMutation(this.getId(model));
    this.addModelToCache(result);
    return result;
  }

  useArchive(
    options: Omit<
      UseMutationOptions<IProject, AxiosError, IProject>,
      "mutationFn"
    > = {},
  ): UseMutationResult<IProject, AxiosError, IProject> {
    return useMutation<IProject, AxiosError, IProject>({
      ...options,
      mutationFn: (model) => this.archive(model),
    });
  }

  delete(): Promise<unknown> {
    throw new UnauthorizedError();
  }

  async get(id: string): Promise<IProject> {
    try {
      return await super.get(id);
    } catch (error) {
      // 404 should be treated as a 403 due to permissions
      if (error.response?.status === 404) {
        error.response.status = 403;
      }
      // 400 is likely an invalid UUID; should be treated as an inaccessible project
      if (error.response?.status === 400) {
        error.response.status = 403;
      }
      throw error;
    }
  }

  public getProjectCreatorEmail(): Promise<{ email: string }> {
    return Promise.resolve({ email: "" });
  }

  public useGetProjectCreatorEmail(
    projectId: string,
    options?: Omit<UseQueryOptions<{ email: string }>, "queryKey" | "queryFn">,
  ): UseQueryResult<{ email: string }> {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(projectId), "creator"],
      queryFn: () => this.getProjectCreatorEmail(),
      enabled: false,
    });
  }

  async restore(model: IProject): Promise<IProject> {
    const result = { ...model, archived: true };
    this.addModelToCache(result);
    await this.request(this.restoreConfig(model));
    this.invalidateAfterMutation(this.getId(model));
    this.addModelToCache(result);
    return result;
  }

  useRestore(
    options: Omit<
      UseMutationOptions<IProject, AxiosError, IProject>,
      "mutationFn"
    > = {},
  ) {
    return useMutation<IProject, AxiosError, IProject>({
      ...options,
      mutationFn: (model) => this.restore(model),
    });
  }

  protected archiveConfig(model: IProject): AxiosRequestConfig {
    return {
      method: "POST",
      url: `${this.endpoint}/${model.id}/archive`,
    };
  }

  protected restoreConfig(model: IProject): AxiosRequestConfig {
    return {
      method: "POST",
      url: `${this.endpoint}/${model.id}/restore`,
    };
  }

  protected createConfig(model: IProject): AxiosRequestConfig {
    return {
      ...super.createConfig(model),
      data: {
        name: model.name,
        description: model.description,
      },
    };
  }

  protected updateConfig(model: IProject): AxiosRequestConfig {
    return {
      ...super.updateConfig(model),
      method: "PATCH" as const,
      data: {
        name: model.name,
        description: model.description,
      },
    };
  }

  protected findConfig(query?: ProjectQuery) {
    return query?.search_params || query?.sort_by
      ? {
          method: "POST",
          url: "/api/v3/projects/search",
          data: {
            search_params: query.search_params,
            sort_by: query.sort_by,
          },
          params: {
            archived: query.archived,
            per_page: query.per_page || this.defaultQuery?.per_page,
            page: query.page || this.defaultQuery?.page,
            teamId: this.teamId,
          },
        }
      : super.findConfig(query);
  }
}
